import React, { useState, useEffect } from 'react';
import organisationKPIsService, { OrganisationKPIs, KPIItem } from '../../services/organisationKPIsService';
import categoryKPIService, { CategoryKPI } from '../../services/categoryKPIService';
import kpiCategoryService, { KPICategory } from '../../services/kpiCategoryService';
import './CreateRuleModal.css';

interface GeneratedRule {
  ruleText: string;
  ruleWeight: number;
  kpis: KPIItem[];
  reasoning: string;
  confidence: number;
}

interface GenerateAIRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRulesGenerated: () => void;
  existingRules: OrganisationKPIs[];
}

const GenerateAIRulesModal: React.FC<GenerateAIRulesModalProps> = ({
  isOpen,
  onClose,
  onRulesGenerated,
  existingRules
}) => {
  const [generating, setGenerating] = useState(false);
  const [generatedRules, setGeneratedRules] = useState<GeneratedRule[]>([]);
  const [availableKPIs, setAvailableKPIs] = useState<CategoryKPI[]>([]);
  const [kpiCategories, setKpiCategories] = useState<KPICategory[]>([]);
  const [selectedRules, setSelectedRules] = useState<boolean[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'options' | 'generating' | 'preview'>('options');

  // Generation options
  const [focusAreas, setFocusAreas] = useState({
    dataConsistency: true,
    businessLogic: true,
    crossKPIValidation: true,
    rangeValidation: true
  });
  const [maxRules, setMaxRules] = useState(5);
  const [priorityLevel, setPriorityLevel] = useState<'conservative' | 'balanced' | 'comprehensive'>('balanced');

  useEffect(() => {
    if (isOpen) {
      fetchAvailableData();
    }
  }, [isOpen]);

  const fetchAvailableData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch categories and their KPIs
      const categories = await kpiCategoryService.getKPICategories();
      setKpiCategories(categories);
      
      // Fetch all KPIs from all categories
      const allKPIs: CategoryKPI[] = [];
      for (const category of categories) {
        try {
          const categoryKPIs = await categoryKPIService.getCategoryKPIs(category.id!);
          allKPIs.push(...categoryKPIs);
        } catch (err) {
          console.warn(`Failed to fetch KPIs for category ${category.category_name}:`, err);
        }
      }
      
      setAvailableKPIs(allKPIs);
    } catch (err) {
      console.error('Error fetching available data:', err);
      setError('Failed to load available KPIs and categories');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentWeightUsed = () => {
    return existingRules.reduce((total, rule) => total + rule.ruleWeight, 0);
  };

  const getRemainingWeight = () => {
    return 100 - getCurrentWeightUsed();
  };

  const handleGenerateRules = async () => {
    try {
      setGenerating(true);
      setError(null);
      setStep('generating');

      // Prepare the data for AI generation
      const requestData = {
        existingRules: existingRules.map(rule => ({
          ruleId: rule.ruleId,
          ruleText: rule.ruleText,
          ruleWeight: rule.ruleWeight,
          kpis: rule.kpis
        })),
        availableKPIs: availableKPIs.map(kpi => ({
          code: kpi.kpiCode,
          name: kpi.kpiName,
          description: kpi.description || '',
          category: kpiCategories.find(cat => cat.id === kpi.categoryId)?.category_name || 'Unknown',
          measurementType: kpi.measurementType
        })),
        kpiCategories: kpiCategories.map(cat => ({
          name: cat.category_name,
          description: '' // KPICategory doesn't have description field
        })),
        remainingWeight: getRemainingWeight(),
        focusAreas,
        maxRules,
        priorityLevel
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/organisation-kpis/generate-ai-rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to generate AI rules: ${response.statusText}`);
      }

      const result = await response.json();
      setGeneratedRules(result.generatedRules || []);
      setSelectedRules(new Array(result.generatedRules?.length || 0).fill(true));
      setStep('preview');
      
    } catch (err) {
      console.error('Error generating AI rules:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate AI rules');
      setStep('options');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSelectedRules = async () => {
    try {
      setGenerating(true);
      setError(null);

      const rulesToSave = generatedRules.filter((_, index) => selectedRules[index]);
      
      if (rulesToSave.length === 0) {
        alert('Please select at least one rule to save');
        return;
      }

      // Save each selected rule
      for (const rule of rulesToSave) {
        await organisationKPIsService.createOrganisationKPI({
          kpis: rule.kpis,
          ruleText: rule.ruleText,
          ruleWeight: rule.ruleWeight
        });
      }

      alert(`Successfully created ${rulesToSave.length} AI-generated rules!`);
      onRulesGenerated();
      handleClose();
      
    } catch (err) {
      console.error('Error saving AI rules:', err);
      setError(err instanceof Error ? err.message : 'Failed to save AI rules');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setStep('options');
    setGeneratedRules([]);
    setSelectedRules([]);
    setError(null);
    onClose();
  };

  const toggleRuleSelection = (index: number) => {
    setSelectedRules(prev => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="create-rule-modal-overlay">
      <div className="create-rule-modal ai-generation-modal">
        <div className="modal-header">
          <div className="modal-title">
            <span className="modal-icon">🤖</span>
            <h2>Generate AI Rules</h2>
          </div>
          <button className="modal-close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message" style={{ 
              background: 'rgba(255, 100, 100, 0.1)', 
              border: '1px solid rgba(255, 100, 100, 0.3)',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#ff6464'
            }}>
              {error}
            </div>
          )}

          {step === 'options' && (
            <>
              <div className="form-section">
                <h3>Current Rule Status</h3>
                <div className="current-rule-status">
                  <div className="weight-info">
                    <div className="weight-stat">
                      <span className="weight-label">Weight Used:</span>
                      <span className="weight-value">{getCurrentWeightUsed()}/100</span>
                    </div>
                    <div className="weight-stat">
                      <span className="weight-label">Remaining:</span>
                      <span className={`weight-value ${getRemainingWeight() < 20 ? 'weight-low' : ''}`}>
                        {getRemainingWeight()}
                      </span>
                    </div>
                  </div>
                  <div className="weight-progress-bar">
                    <div 
                      className={`weight-progress-fill ${getCurrentWeightUsed() > 80 ? 'weight-high' : ''}`}
                      style={{ width: `${getCurrentWeightUsed()}%` }}
                    />
                  </div>
                </div>

                <h3>Generation Options</h3>
                
                <div className="form-group">
                  <label>Focus Areas</label>
                  <div className="focus-areas-grid">
                    {Object.entries(focusAreas).map(([key, value]) => (
                      <label key={key} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => setFocusAreas(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">
                          {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="maxRules">Maximum Rules to Generate</label>
                    <input
                      type="number"
                      id="maxRules"
                      value={maxRules}
                      onChange={(e) => setMaxRules(parseInt(e.target.value) || 1)}
                      min="1"
                      max="10"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="priorityLevel">Priority Level</label>
                    <select
                      id="priorityLevel"
                      value={priorityLevel}
                      onChange={(e) => setPriorityLevel(e.target.value as any)}
                      className="form-input form-select"
                    >
                      <option value="conservative">Conservative (High-impact rules only)</option>
                      <option value="balanced">Balanced (Mix of important rules)</option>
                      <option value="comprehensive">Comprehensive (Detailed validation)</option>
                    </select>
                  </div>
                </div>


              </div>
            </>
          )}

          {step === 'generating' && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
              <h3 style={{ color: '#40e0ff', marginBottom: '12px' }}>Generating AI Rules...</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Lumina AI is analyzing your KPIs and creating intelligent validation rules
              </p>
            </div>
          )}

          {step === 'preview' && (
            <div className="preview-layout">
              <div className="preview-left">
                <div className="form-section">
                  <h3>Current Rule Status</h3>
                  <div className="current-rule-status">
                    <div className="weight-info">
                      <div className="weight-stat">
                        <span className="weight-label">Weight Used:</span>
                        <span className="weight-value">{getCurrentWeightUsed()}/100</span>
                      </div>
                      <div className="weight-stat">
                        <span className="weight-label">Remaining:</span>
                        <span className={`weight-value ${getRemainingWeight() < 20 ? 'weight-low' : ''}`}>
                          {getRemainingWeight()}
                        </span>
                      </div>
                    </div>
                    <div className="weight-progress-bar">
                      <div 
                        className={`weight-progress-fill ${getCurrentWeightUsed() > 80 ? 'weight-high' : ''}`}
                        style={{ width: `${getCurrentWeightUsed()}%` }}
                      />
                    </div>
                  </div>

                  <h3>Generation Options</h3>
                  
                  <div className="form-group">
                    <label>Focus Areas</label>
                    <div className="focus-areas-grid">
                      {Object.entries(focusAreas).map(([key, value]) => (
                        <label key={key} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => setFocusAreas(prev => ({ ...prev, [key]: e.target.checked }))}
                            className="checkbox-input"
                            disabled
                          />
                          <span className="checkbox-text">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="maxRules">Maximum Rules Generated</label>
                      <input
                        type="number"
                        id="maxRules"
                        value={maxRules}
                        className="form-input"
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="priorityLevel">Priority Level</label>
                      <select
                        id="priorityLevel"
                        value={priorityLevel}
                        className="form-input form-select"
                        disabled
                      >
                        <option value="conservative">Conservative (High-impact rules only)</option>
                        <option value="balanced">Balanced (Mix of important rules)</option>
                        <option value="comprehensive">Comprehensive (Detailed validation)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="preview-right">
                <div className="form-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3>Generated Rules Preview</h3>
                    <span style={{ color: '#40e0ff' }}>
                      {selectedRules.filter(Boolean).length} of {generatedRules.length} selected
                    </span>
                  </div>

                  <div style={{ maxHeight: '700px', overflowY: 'auto', gap: '20px', display: 'flex', flexDirection: 'column' }}>
                    {generatedRules.map((rule, index) => (
                    <div 
                      key={index}
                      style={{
                        border: `2px solid ${selectedRules[index] ? '#40e0ff' : 'rgba(255, 255, 255, 0.2)'}`,
                        borderRadius: '12px',
                        padding: '16px',
                        background: selectedRules[index] ? 'rgba(64, 224, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => toggleRuleSelection(index)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <input
                            type="checkbox"
                            checked={selectedRules[index]}
                            onChange={() => toggleRuleSelection(index)}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span style={{ 
                            background: 'rgba(64, 224, 255, 0.3)',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}>
                            Weight: {rule.ruleWeight}
                          </span>
                          <span style={{ 
                            background: `rgba(${rule.confidence > 80 ? '0, 255, 0' : rule.confidence > 60 ? '255, 165, 0' : '255, 0, 0'}, 0.3)`,
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}>
                            Confidence: {rule.confidence}%
                          </span>
                        </div>
                      </div>
                      
                      <h4 style={{ margin: '0 0 8px 0', color: '#ffffff' }}>{rule.ruleText}</h4>
                      
                      <p style={{ 
                        margin: '0 0 12px 0', 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.9rem',
                        fontStyle: 'italic'
                      }}>
                        {rule.reasoning}
                      </p>
                      
                      <div>
                        <strong style={{ color: '#40e0ff' }}>Associated KPIs ({rule.kpis.length}):</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {rule.kpis.map((kpi, kpiIndex) => (
                            <span 
                              key={kpiIndex}
                              style={{
                                background: 'rgba(64, 224, 255, 0.2)',
                                padding: '4px 8px',
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                border: '1px solid rgba(64, 224, 255, 0.3)'
                              }}
                            >
                              {kpi.name} ({kpi.code})
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="cancel-button"
            onClick={handleClose}
            disabled={generating}
          >
            Cancel
          </button>
          
          {step === 'options' && (
            <button 
              className="save-button"
              onClick={handleGenerateRules}
              disabled={generating || loading || getRemainingWeight() <= 0}
            >
              {generating ? 'Generating...' : 'Generate AI Rules'}
            </button>
          )}
          
          {step === 'preview' && (
            <>
              <button 
                className="cancel-button"
                onClick={() => setStep('options')}
                disabled={generating}
                style={{ marginRight: '8px' }}
              >
                Back to Options
              </button>
              <button 
                className="save-button"
                onClick={handleSaveSelectedRules}
                disabled={generating || selectedRules.filter(Boolean).length === 0}
              >
                {generating ? 'Saving...' : `Save ${selectedRules.filter(Boolean).length} Rules`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateAIRulesModal;
